import { useTranslation } from "react-i18next";
import {
  formatDateForDisplay,
  formatTimeDisplay,
  formatTimeForDisplay,
} from "helpers/calendarUtils";
import {
  CopyIcon,
  ExternalLinkIcon,
  ClockIcon,
  CalendarIcon,
  UserIcon,
  MapPinIcon,
  UsersIcon,
} from "./svgIcons";
import styles from "./styles.module.scss";

const GoogleEventModal = ({ event, onClose }) => {
  const { t } = useTranslation("global");

  if (!event) return null;

  // Extract data from the formatted event structure
  const eventData = {
    title: event.title,
    startDate: event.start,
    endDate: event.end,
    place: event.extendedProps?.place || null,
    capacity: event.extendedProps?.capacity || 0,
    customer: event.extendedProps?.customer || null,
    googleEventId: event.extendedProps?.googleEventId || null,
  };

  // Format date and time from the Date objects
  const formatDate = (date) => {
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCopyDetails = () => {
    const details = [
      `${t("calendar.googleEventModal.copy.event")}: ${eventData.title}`,
      `${t("calendar.googleEventModal.copy.date")}: ${formatDate(new Date(eventData.startDate))}`,
      `${t("calendar.googleEventModal.copy.time")}: ${formatTime(new Date(eventData.startDate))} - ${formatTime(new Date(eventData.endDate))}`,
    ];

    if (eventData.place) {
      details.push(`${t("calendar.googleEventModal.copy.location")}: ${eventData.place.name}`);
      if (eventData.place.address) {
        details.push(`${t("calendar.googleEventModal.copy.address")}: ${eventData.place.address.address}, ${eventData.place.address.city}, ${eventData.place.address.country}`);
      }
    }

    if (eventData.customer) {
      details.push(`${t("calendar.googleEventModal.copy.customer")}: ${eventData.customer.firstName} ${eventData.customer.lastName}`);
      if (eventData.customer.company) details.push(`${t("calendar.googleEventModal.copy.company")}: ${eventData.customer.company}`);
      if (eventData.customer.email) details.push(`${t("calendar.googleEventModal.copy.email")}: ${eventData.customer.email}`);
      if (eventData.customer.phone) details.push(`${t("calendar.googleEventModal.copy.phone")}: ${eventData.customer.phone}`);
    }

    if (eventData.capacity > 0) {
      details.push(`${t("calendar.googleEventModal.copy.capacity")}: ${eventData.capacity} ${t("calendar.googleEventModal.sections.capacity.people")}`);
    }

    navigator.clipboard.writeText(details.join('\n'))
      .then(() => {
        alert(t("calendar.googleEventModal.alerts.copySuccess"));
      })
      .catch(err => {
        console.error('Failed to copy details: ', err);
        alert(t("calendar.googleEventModal.alerts.copyError"));
      });
  };

  const handleOpenInCalendar = () => {
    if (eventData.googleEventId) {
      window.open(`https://calendar.google.com/calendar/u/0/r/event/${eventData.googleEventId}`, '_blank');
    } else {
      const startDate = new Date(eventData.startDate).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      const endDate = new Date(eventData.endDate).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      
      let location = '';
      if (eventData.place) {
        location = eventData.place.name;
        if (eventData.place.address) {
          location += `, ${eventData.place.address.address}, ${eventData.place.address.city}, ${eventData.place.address.country}`;
        }
      }
      
      const url = new URL('https://www.google.com/calendar/render');
      url.searchParams.append('action', 'TEMPLATE');
      url.searchParams.append('text', eventData.title);
      url.searchParams.append('dates', `${startDate}/${endDate}`);
      if (location) url.searchParams.append('location', location);
      
      window.open(url.toString(), '_blank');
    }
  };

  const handleViewOnMap = () => {
    if (!eventData.place) return;
    
    let query = eventData.place.name;
    if (eventData.place.address) {
      query = `${eventData.place.address.address}, ${eventData.place.address.city}, ${eventData.place.address.country}`;
    }
    
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_blank');
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.eventTypeBadge}>
            <span className={styles.googleLogo}>G</span>
            {t("calendar.googleEventModal.eventTypeBadge")}
          </div>
          <h2 className={styles.title}>{eventData.title || t("calendar.googleEventModal.untitledEvent")}</h2>
          <button onClick={onClose} className={styles.closeButton} aria-label={t("calendar.googleEventModal.closeButton")}>
            &times;
          </button>
        </div>

        <div className={styles.content}>
          {/* Date and Time Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <CalendarIcon className={styles.sectionIcon} />
              {t("calendar.googleEventModal.sections.dateTime.title")}
            </h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.googleEventModal.sections.dateTime.date")}</span>
                <span className={styles.detailValue}>
                  {formatDate(new Date(eventData.startDate))}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.googleEventModal.sections.dateTime.start")}</span>
                <span className={styles.detailValue}>
                  {formatTime(new Date(eventData.startDate))}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.googleEventModal.sections.dateTime.end")}</span>
                <span className={styles.detailValue}>
                  {formatTime(new Date(eventData.endDate))}
                </span>
              </div>
            </div>
          </div>

          {/* Location Section */}
          {eventData.place && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <MapPinIcon className={styles.sectionIcon} />
                {t("calendar.googleEventModal.sections.location.title")}
              </h3>
              <div className={styles.locationCard}>
                <div className={styles.locationName}>{eventData.place.name}</div>
                {eventData.place.address && (
                  <div className={styles.locationAddress}>
                    {eventData.place.address.address}
                    {eventData.place.address.city && `, ${eventData.place.address.city}`}
                    {eventData.place.address.country && `, ${eventData.place.address.country}`}
                  </div>
                )}
                <button className={styles.mapLink} onClick={handleViewOnMap}>
                  <ExternalLinkIcon className={styles.linkIcon} />
                  {t("calendar.googleEventModal.sections.location.viewOnMap")}
                </button>
              </div>
            </div>
          )}

          {/* Customer Section */}
          {eventData.customer && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <UserIcon className={styles.sectionIcon} />
                {t("calendar.googleEventModal.sections.customer.title")}
              </h3>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>{t("calendar.googleEventModal.sections.customer.name")}</span>
                  <span className={styles.detailValue}>
                    {eventData.customer.firstName} {eventData.customer.lastName}
                  </span>
                </div>
                {eventData.customer.company && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>{t("calendar.googleEventModal.sections.customer.company")}</span>
                    <span className={styles.detailValue}>{eventData.customer.company}</span>
                  </div>
                )}
                {eventData.customer.email && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>{t("calendar.googleEventModal.sections.customer.email")}</span>
                    <span className={styles.detailValue}>
                      <a href={`mailto:${eventData.customer.email}`}>{eventData.customer.email}</a>
                    </span>
                  </div>
                )}
                {eventData.customer.phone && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>{t("calendar.googleEventModal.sections.customer.phone")}</span>
                    <span className={styles.detailValue}>
                      <a href={`tel:${eventData.customer.phone}`}>{eventData.customer.phone}</a>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Capacity */}
          {eventData.capacity > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <UsersIcon className={styles.sectionIcon} />
                {t("calendar.googleEventModal.sections.capacity.title")}
              </h3>
              <div className={styles.capacityBadge}>
                <span className={styles.capacityNumber}>{eventData.capacity}</span>
                <span className={styles.capacityLabel}>{t("calendar.googleEventModal.sections.capacity.people")}</span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.secondaryButton} onClick={handleCopyDetails}>
            <CopyIcon className={styles.buttonIcon} />
            {t("calendar.googleEventModal.buttons.copyDetails")}
          </button>
          <button className={styles.primaryButton} onClick={handleOpenInCalendar}>
            <ExternalLinkIcon className={styles.buttonIcon} />
            {t("calendar.googleEventModal.buttons.openInCalendar")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoogleEventModal;